/**
 * InventoryCreateView — /inventory/new/:tab  재고 등록 페이지 (목업)
 *
 * 탭 둘 — 파트 한 장 · 서버 한 대. 서버 한 대 = ⓪ 사진 상자 · ① 카탈로그 · ② 실물 · ③ 구성(ServerConfigEditor) + 저장 버튼.
 * 실제: ① 카탈로그 — 모델명 · PN 을 치면 자동완성(SKU · 기종), SKU 를 고르면 기종까지 정해지고 사양 단만 남는다. 기종을 고르면 SKU 칩 단(2026-10-02 A′).
 * 목업: 틀 · 후보는 inventory-api 의 가짜 응답, 사진은 버튼 하나가 가짜로 읽는다(읽은 PN 으로 같은 검색을 탄다), 저장은 요약만 띄운다.
 * 근거: hcltech-parts docs/INVENTORY_SCREENS.md §A1-서버 · docs/VOCABULARY.md "재고 등록 — 서버 한 대 목업".
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import { findMenuPlace, menuHeading, renderMenuTabs } from '../components/menu';
import { ServerConfigEditor } from '../components/ServerConfigEditor';
import type { Included, Installed } from '../components/ServerConfigEditor';
import { PREVIEW } from '../data/preview-inventory';
import { assert } from '../lib/assert';
import { detailOf } from '../lib/events';
import { btoIncluded, frameOf, inspectDoneCount, INSPECT_TOTAL, linesOfTarget, MOCK_PURCHASE_LINES, nextFrameTypeOf, renderCatalog, renderCatalogFound, renderInspect, renderPhoto, renderProgress, renderSaved, renderScan, renderSuggest, renderTabs, renderUnit, targetOf } from './inventory-create/render';
import { CATALOG_STEPS, INVENTORY_CREATE_TABS, type CatalogStep, type InventoryCreateData, type InventoryCreateTab, type PhotoLine, type ScanBrand, type ScanLine, type ScanTarget, type ScanType, type ServerSection, type UnitInput } from './inventory-create/types';

function isTab(raw: string): raw is InventoryCreateTab {
  return INVENTORY_CREATE_TABS.some((t) => t === raw);
}

function isCatalogStep(raw: string | undefined): raw is CatalogStep {
  return CATALOG_STEPS.some((t) => t === raw);
}

/** 입고 메뉴 안의 이 화면 자리 — 재고 등록 탭. 파트 · 서버 · 구매 건 주소 전부 이 아래라 탭 줄이 같다 */
const REGISTER_HREF = '/inventory/new';

const EMPTY_UNIT: UnitInput = { serial_number: '', condition: '', warehouse: '본사', zone: '', cost_amount: '', cost_currency: 'KRW', origin: 'found', security_id: '', tracking_number: '', manufacture_date: '' };

/** 목업 — 사진 3장(풀아웃 태그 · iLO 라벨 · 부품 라벨 묶음)이 읽은 줄. 섀시 5칸 + 부품 7종. 실제는 서버의 읽기 경계(Zod)가 같은 꼴로 준다 */
function mockPhotoLines(): PhotoLine[] {
  const part = (option_part: string, category: string, description: string, qty: number, serial: string | null = null): PhotoLine =>
    ({ kind: 'part', part: { vendor: 'HPE', option_part, category, description, serial, unit_id: null }, qty, keep: true });
  return [
    { kind: 'chassis', field: 'catalog.query',         label: '제품 번호(PN)', value: '868703-B21',          keep: true },
    { kind: 'chassis', field: 'unit.serial_number',    label: '시리얼(S/N)',   value: 'CZ20350K7X',          keep: true },
    { kind: 'chassis', field: 'unit.security_id',      label: 'iLO Security ID', value: '3K7X-9QW2-ABCD-EF12', keep: true },
    { kind: 'chassis', field: 'unit.tracking_number',  label: 'CT 번호',       value: '7CE0350K7X',          keep: true },
    { kind: 'chassis', field: 'unit.manufacture_date', label: '제조일',        value: '2020-05-19',          keep: true },
    part('P02510-B21', 'cpu',             'Intel Xeon-Gold 6248R (3.0GHz/24-core/205W)',   2),
    part('P00924-B21', 'memory',          'HPE 32GB 2Rx4 PC4-2933Y-R Smart Kit',           12),
    part('P18434-B21', 'ssd',             'HPE 960GB SATA RI SFF SC MV SSD',               4),
    part('804331-B21', 'raid_controller', 'HPE Smart Array P408i-a SR Gen10 Controller',   1),
    part('P08437-B21', 'network_adapter', 'HPE Ethernet 10/25Gb 2-port 640FLR-SFP28 OCP',  1),
    part('865414-B21', 'power_supply',    'HPE 800W Flex Slot Platinum Hot Plug PSU',      2),
    part('733660-B21', 'rail_kit',        'HPE 2U SFF Easy Install Rail Kit',              1),
  ];
}

/** 읽은 부품 줄 → 라벨 한 장 = 한 장. 시리얼은 라벨에 있는 것(SSD · 일부 DIMM)만 — 목업이라 앞 몇 장에만 붙인다 */
function expandPhotoParts(lines: PhotoLine[]): api.PartCandidate[] {
  const serialsOf: Record<string, string[]> = { 'P18434-B21': ['S4XQNB70K', 'S4XQNB71L', 'S4XQNB72M', 'S4XQNB73N'], 'P00924-B21': ['8A1B2C3D', '8A1B2C3E'] };   // 재고 카드의 S4XQNA0N 과 안 겹치게
  const out: api.PartCandidate[] = [];
  for (const line of lines) {
    if (line.kind !== 'part' || !line.keep) continue;
    for (let n = 0; n < line.qty; n++) out.push({ ...line.part, serial: serialsOf[line.part.option_part]?.[n] ?? null });
  }
  return out;
}

/** 목업 — 이 SKU 로 이미 등록된 구성 하나(실제 hclt_product 는 아직 0행). 사진을 확인한 뒤에만 보인다 — 읽은 부품과 같은 구성 */
function mockRegisteredProduct(chassisId: number): api.HcltProductView {
  return { id: '42', hclt_number: 'HCL-SV-000042', description: '6248R ×2 · 32GB ×12 · 960GB SSD ×4 · P408i-a · 640FLR · 800W ×2', chassis_id: chassisId, unit_count: 3 };
}

export class InventoryCreateView extends SolaView<InventoryCreateData> {
  override events = {
    click:   { '@photo-mock': 'handlePhotoMock', '@photo-line': 'handlePhotoLine', '@photo-confirm': 'handlePhotoConfirm', '@match': 'handleMatch', '@catalog-reopen': 'handleCatalogReopen', '@save': 'handleSave', '@next-unit': 'handleNextUnit', '@scan-purchase': 'handleScanPurchase', '@scan-purchase-line': 'handleScanPurchaseLine', '@scan-purchase-all': 'handleScanPurchaseAll', '@scan-new-target': 'handleScanNewTarget', '@scan-place-all': 'handleScanPlaceAll', '@scan-mock': 'handleScanMock',
               '@bubble-remove': 'handleBubbleRemove', '@bubble-out': 'handleBubbleOut', '@bubble-close': 'handleBubbleClose', '@target-remove': 'handleTargetRemove', '@bubble': 'handleBubble', '@target': 'handleTarget' },   // 버블 · 카드 안의 버튼이 버블 · 카드보다 먼저, 버블이 카드보다 먼저
    input:   { '@catalog-query': 'handleCatalogQuery', '@unit': 'handleUnit', '@inspect': 'handleInspect' },
    dragstart: { '@chip': 'handleChipDragStart', '@bubble': 'handleBubbleDragStart' },
    dragover:  { '@slot': 'handleDragOver', '@target': 'handleDragOver', '@pool': 'handleDragOver' },
    drop:      { '@slot': 'handleDropOnSlot', '@target': 'handleDropOnTarget', '@pool': 'handleDropOnTarget' },
    keydown: { '@catalog-query': 'handleCatalogKey', '@scan-input': 'handleScanKey' },
    change:  { '@sku': 'handleSku', '@product': 'handleProduct', '@unit': 'handleUnit', '@inspect': 'handleInspect', '@scan-brand': 'handleScanBrand' },
    'config:change': { '*': 'handleConfigChange' },
  };

  private readonly editor = new ServerConfigEditor(api.mockChassisFrame(), api.mockPartCandidates());
  private config: { installed: Installed[]; included: Included[] } = { installed: [], included: [] };
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private searchSeq = 0;      // 늦게 온 응답을 버리는 번호 — 치는 동안 검색이 겹친다

  /** purchaseOrNull = 입고 탭의 [입고] 로 들어온 구매 건(PO 번호) — 그 줄의 구매 품목 카드를 깔고 연다 (현민 2026-10-08). 메뉴 · 탭으로 들어오면 null */
  constructor(args: { tab: string; purchaseOrNull: string | null }) {
    super();
    assert(isTab(args.tab), `주소창의 tab 은 part · server 중 하나여야 한다. 받은 값: ${args.tab}`);
    this.data = {
      tab: args.tab,
      server: {
        catalog: { query: '', model: null, sku: null, bto: null, productId: null },
        catalogOptions: { matches: [], skus: [], preconfigs: [], products: [], modelDetail: null, skuDetail: null },
        catalogStep: 'model',
        unit: { ...EMPTY_UNIT }, fromPhoto: new Set(), photo: { status: 'idle', lines: [], error: '' }, scan: { brand: 'HPE', lines: [], targets: [], activeTarget: null, open: null, selected: new Set(), isPurchaseOpen: false },
        configCount: { installed: 0, included: 0 }, saved: null, inspect: {},
      },
      part: { note: '' },
    };
    if (args.purchaseOrNull !== null) this.addPurchaseCards(args.purchaseOrNull);
  }

  /** 구매 건 하나의 줄들을 카드로 — 서버 한 대 줄은 안의 부품(섀시 먼저)이 카드, 부품 줄은 그 줄이 카드 */
  private addPurchaseCards(po: string): void {
    const lines = PREVIEW.PO_LINES.filter((line) => line.po === po);
    assert(lines.length > 0, `구매 건이 없다: ${po} — 입고 탭의 줄에서만 온다`);
    for (const line of lines) {
      if (!line.is_server) { this.addTarget({ key: `purchase-${line.option_part}`, brand: 'HPE', option_part: line.option_part, description: line.description, category: null, qty: line.qty - line.received, source: 'purchase' }); continue; }
      line.parts.forEach((part, index) => this.addTarget({ key: `purchase-${part.option_part}`, brand: 'HPE', option_part: part.option_part, description: part.description, category: index === 0 ? 'chassis' : null, qty: part.qty, source: 'purchase' }));
    }
  }

  override getTitle(): string { return '입고 › 재고 등록 — HCLTech 어드민'; }

  render(): HTMLElement {
    const place = findMenuPlace(REGISTER_HREF);
    assert(place !== null, `재고 등록이 메뉴에 없다: ${REGISTER_HREF} — MENU_GROUPS 입고의 탭에 있어야 한다`);
    return htmlSolaWithInstance(this)`
      <div class="page page-inventory-create">
        <header class="page-header ic-page-header">
          <h1>${menuHeading(place)}</h1>
          ${renderMenuTabs(place)}
          ${this.data.tab === 'server' ? `<div class="ic-progress-wrap">${renderProgress(this.data.server)}</div>` : ''}
        </header>
        ${renderTabs(this.data.tab)}
        <section class="page-body ic-body">${this.data.tab === 'server' ? this.renderServer() : this.renderPart()}</section>
      </div>`;
  }

  private renderServer(): string {
    const s = this.data.server;
    return `
      <div class="ic-server">
        <div class="ic-head">${renderScan(s)}${renderPhoto(s)}${renderCatalog(s)}${renderUnit(s)}</div>
        <section class="form-card ic-card sr-config">
          <h2>③ 구성 <small>안에 든 것 — 칸에 끌어 놓거나, 📷 읽은 것을 전부 배치. 몰라도 비워 두고 저장할 수 있습니다</small></h2>
          <div class="ic-editor"></div>
        </section>
        ${renderInspect(s)}
        <footer class="form-actions ic-save">
          <span class="text-muted">저장하면 이 한 대가 재고 목록에 생깁니다 — 서버 한 행, 안에 든 부품은 그 아래 행으로</span>
          <button type="button" class="btn-primary" data-action="save">저장</button>
        </footer>
        <div class="ic-saved-slot">${s.saved === null ? '' : renderSaved(s.saved)}</div>
      </div>`;
  }

  private renderPart(): string {
    return `<div class="placeholder-card"><h2>파트 한 장 — 다음 걸음(A1-파트)</h2><p class="text-muted">서버 한 대 탭의 ② 실물과 같은 폼에 부품 한 줄. 목업은 서버부터.</p></div>`;
  }

  override onMount(): void { this.mountEditor(); }

  private mountEditor(): void {
    const host = this._rootElement?.querySelector('.ic-editor');
    if (!host) return;
    host.replaceChildren(this.editor.render());
    this.editor.onMount();
  }

  // ===== 이벤트 =====

  /** 사진 올리기 — 목업: 0.9초 뒤 읽은 줄이 패널에 뜬다. 실제는 파일을 서버 읽기 경계로 보내고 같은 꼴을 받는다 */
  handlePhotoMock(): void {
    const s = this.data.server;
    s.photo = { status: 'reading', lines: [], error: '' };
    this.rerenderHead();
    window.setTimeout(() => {
      s.photo = { status: 'read', lines: mockPhotoLines(), error: '' };
      this.rerenderHead();
    }, 900);
  }

  /** 읽은 줄 하나를 뺀다 · 되살린다 */
  handlePhotoLine(_e: Event, el: HTMLElement): void {
    const n = Number(el.dataset.index);
    const line = this.data.server.photo.lines[n];
    assert(line !== undefined, `읽은 줄 ${n} 이 없다`);
    line.keep = !line.keep;
    this.rerenderHead();
  }

  /** 확인 — 섀시 줄은 ① ② 의 칸으로(📷 표시), 부품 줄은 ③ 트레이로. PN 줄이 있으면 그 PN 으로 검색해 SKU 를 고른다 */
  handlePhotoConfirm(): void {
    const s = this.data.server;
    const kept = s.photo.lines.filter((l) => l.keep);
    const chassis = kept.filter((l): l is Extract<PhotoLine, { kind: 'chassis' }> => l.kind === 'chassis');
    const unit: UnitInput = { ...s.unit };
    for (const line of chassis) {
      if (line.field === 'unit.serial_number') unit.serial_number = line.value;
      if (line.field === 'unit.security_id') unit.security_id = line.value;
      if (line.field === 'unit.tracking_number') unit.tracking_number = line.value;
      if (line.field === 'unit.manufacture_date') unit.manufacture_date = line.value;
    }
    s.unit = unit;
    s.fromPhoto = new Set(chassis.map((l) => l.field));
    s.photo = { ...s.photo, status: 'confirmed' };
    this.editor.setPhotoParts(expandPhotoParts(kept));
    this.addPhotoTargets(kept);
    const pn = chassis.find((l) => l.field === 'catalog.query');
    if (pn !== undefined) {
      s.catalog = { query: pn.value, model: null, sku: null, bto: null, productId: null };
      s.catalogOptions = { matches: [], skus: [], preconfigs: [], products: [], modelDetail: null, skuDetail: null };
      s.catalogStep = 'model';
    }
    this.rerenderHead();
    if (pn !== undefined) void this.searchMatches().then(() => this.pickMatchFromPhoto(pn.value));
  }

  /** 사진이 읽은 PN 과 같은 SKU 제안이 있으면 고른다. 없으면 제안 목록을 그대로 두고 사람이 고른다 */
  private async pickMatchFromPhoto(pn: string): Promise<void> {
    const hit = this.data.server.catalogOptions.matches.find((m) => m.kind === 'sku' && m.sku.option_part === pn);
    if (hit === undefined) return;
    await this.selectMatch(hit);
    this.data.server.fromPhoto.add('catalog.sku');
    this.rerenderFound();
    this.rerenderProgress();
  }

  /** 치는 동안 280ms 쉬면 자동완성. 치기 시작하면 고른 기종 · SKU · 사양은 비운다(다른 걸 찾는 중) */
  handleCatalogQuery(_e: Event, el: HTMLElement): void {
    const s = this.data.server;
    s.catalog = { query: (el as HTMLInputElement).value, model: null, sku: null, bto: null, productId: null };
    s.catalogOptions = { matches: [], skus: [], preconfigs: [], products: [], modelDetail: null, skuDetail: null };
    s.catalogStep = 'model';
    s.fromPhoto.delete('catalog.query');
    s.fromPhoto.delete('catalog.sku');
    this.rerenderSuggest();
    this.rerenderFound();
    if (this.searchTimer !== null) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => { this.searchTimer = null; void this.searchMatches(); }, 280);
  }

  /** Enter = 첫 제안. Escape = 제안 닫기(목록만 비움) */
  handleCatalogKey(e: Event): void {
    const key = (e as KeyboardEvent).key;
    const first = this.data.server.catalogOptions.matches[0];
    if (key === 'Enter' && first !== undefined) { e.preventDefault(); void this.selectMatch(first).then(() => this.rerenderFound()); }
    if (key === 'Escape') { this.data.server.catalogOptions = { ...this.data.server.catalogOptions, matches: [] }; this.rerenderSuggest(); }
  }

  private async searchMatches(): Promise<void> {
    const seq = ++this.searchSeq;
    const matches = await api.fetchCatalogMatches(this.data.server.catalog.query);
    if (seq !== this.searchSeq) return;
    this.data.server.catalogOptions = { matches, skus: [], preconfigs: [], products: [], modelDetail: null, skuDetail: null };
    this.rerenderSuggest();
  }

  handleMatch(_e: Event, el: HTMLElement): void {
    const index = Number(el.dataset.index);
    const match = this.data.server.catalogOptions.matches[index];
    assert(match !== undefined, `제안 ${index} 가 목록에 없다`);
    void this.selectMatch(match).then(() => this.rerenderFound());
  }

  /** 제안 하나를 고른다 — 모델명이 정해지고 제안 목록이 닫힌다. SKU 제안이면 SKU 까지 정해져 구성 단, 모델명 제안이면 SKU 단.
   *  그 모델명의 SKU 목록 · 등록된 구성 · 모델 상세(+ SKU 면 SKU 상세)를 한 번에 받는다 */
  private async selectMatch(match: api.CatalogMatch): Promise<void> {
    const s = this.data.server;
    const sku = match.kind === 'sku' ? match.sku : null;
    const bto = match.kind === 'bto' ? match.bto : null;
    s.catalog = { ...s.catalog, model: match.model, sku, bto, productId: null };
    s.catalogOptions = { ...s.catalogOptions, skus: [], preconfigs: [], products: [], modelDetail: null, skuDetail: null };
    s.catalogStep = match.kind === 'model' ? 'sku' : 'product';
    s.fromPhoto.delete('catalog.sku');
    if (bto !== null) this.applyBto(bto);
    this.rerenderSuggest();
    this.rerenderFound();
    const detailId = sku !== null ? sku.id : bto !== null ? bto.base_chassis_id : null;
    const [skus, preconfigs, products, modelDetail, skuDetail] = await Promise.all([
      api.fetchChassisSkus(match.model.id),
      api.fetchChassisPreconfigs(match.model.id),
      api.fetchServerProducts(match.model.id),
      api.fetchChassisModelDetail(match.model.id),
      detailId === null ? Promise.resolve(null) : api.fetchChassisDetail(detailId),
    ]);
    if (s.catalog.model !== match.model) return;      // 받는 사이 다른 걸 골랐다
    const bMockProduct = s.photo.status === 'confirmed' && sku !== null && products.length === 0;   // 목업 — 등록된 구성이 아직 0행이라 사진 뒤엔 하나를 보여 준다
    s.catalogOptions = { ...s.catalogOptions, skus, preconfigs, products: bMockProduct ? [mockRegisteredProduct(sku.id)] : products, modelDetail, skuDetail };
    this.rerenderFound();
    this.rerenderProgress();
  }

  /** BTO 를 골랐다 — 기본 포함(CPU · 메모리 · 컨트롤러 · PSU)을 ③ 에 「기본 포함」 칩으로 미리 앉힌다. CTO 로 바꾸면 빈다 */
  private applyBto(bto: api.PreconfigView): void {
    const parts: api.PartCandidate[] = [];
    for (const x of btoIncluded(bto)) for (let n = 0; n < x.qty; n++) parts.push({ vendor: 'HPE', option_part: x.option_part, category: x.category, description: x.description, serial: null, unit_id: null });
    this.editor.setBaseParts(parts);
  }

  /** SKU 단 — 값 'cto:<PN>' · 'bto:<id>' · ''(모름). BTO 면 기본 포함이 ③ 에 앉고, 아니면 ③ 의 기본 포함은 빈다 */
  handleSku(_e: Event, el: HTMLElement): void {
    const v = (el as HTMLInputElement).value;
    const s = this.data.server;
    const bBto = v.startsWith('bto:');
    const k = !v.startsWith('cto:') ? null : s.catalogOptions.skus.find((x) => x.option_part === v.slice('cto:'.length));
    const b = !bBto ? null : s.catalogOptions.preconfigs.find((x) => String(x.id) === v.slice('bto:'.length));
    assert(k !== undefined && b !== undefined, `고른 SKU ${v} 가 이 기종의 목록에 없다`);
    s.catalog.sku = k;
    s.catalog.bto = b;
    s.catalogStep = 'product';
    s.fromPhoto.delete('catalog.sku');
    s.catalogOptions.skuDetail = null;
    if (b !== null) this.applyBto(b); else this.editor.setBaseParts([]);
    this.rerenderFound();
    const detailId = k !== null ? k.id : b !== null ? b.base_chassis_id : null;
    if (detailId !== null) void this.loadSkuDetail(detailId);
  }

  /** 고른 SKU 의 상세. 받는 사이 다른 SKU 를 골랐으면 버린다 */
  private async loadSkuDetail(chassisId: number): Promise<void> {
    const detail = await api.fetchChassisDetail(chassisId);
    const { sku, bto } = this.data.server.catalog;
    const wanted = sku !== null ? sku.id : bto !== null ? bto.base_chassis_id : null;
    if (wanted !== chassisId) return;
    this.data.server.catalogOptions.skuDetail = detail;
    this.rerenderFound();
  }

  /** 접힌 단의 바꾸기 — 그 단만 다시 펼친다. 고른 값은 그대로(다시 고르면 그때 바뀐다) */
  handleCatalogReopen(_e: Event, el: HTMLElement): void {
    const step = el.dataset.step;
    assert(isCatalogStep(step), `바꾸기 버튼의 data-step 이 단 이름이 아니다: ${step}`);
    this.data.server.catalogStep = step;
    this.rerenderFound();
  }

  /** 등록된 구성 고르기 — 그 구성으로 ③ 이 미리 찬다(목업: 📷 읽은 부품을 전부 배치. 실제는 그 구성의 줄 hclt_product_item). 새 구성이면 ③ 은 빈 채로 */
  handleProduct(_e: Event, el: HTMLElement): void {
    const v = (el as HTMLInputElement).value;
    this.data.server.catalog.productId = v === '' ? null : v;
    this.data.server.catalogStep = null;        // 셋 다 정해졌다 — 전부 접힘
    this.editor.setConfig([], []);
    if (v !== '') this.editor.placeAll();
    this.rerenderFound();
    this.rerenderProgress();
  }

  handleUnit(_e: Event, el: HTMLElement): void {
    const field = el.dataset.field as keyof UnitInput | undefined;
    assert(field !== undefined, '실물 칸에 data-field 가 없다');
    const value = (el as HTMLInputElement).value;
    const unit: UnitInput = { ...this.data.server.unit, [field]: value };
    this.data.server.unit = unit;
    this.data.server.fromPhoto.delete(`unit.${field}`);
  }

  /** ④ 점검 후보의 답 — 체크 · 고르기 · 적기를 상태에만(목업). 머리의 "답한 것" 수만 새로 */
  handleInspect(_e: Event, el: HTMLElement): void {
    const key = el.dataset.key;
    assert(key !== undefined, '점검 후보 칸에 data-key 가 없다');
    const prev = this.data.server.inspect[key] ?? { done: false, choice: '', text: '' };
    const input = el as HTMLInputElement;
    const next = input.type === 'checkbox' ? { ...prev, done: input.checked } : el.tagName === 'SELECT' ? { ...prev, choice: input.value } : { ...prev, text: input.value };
    this.data.server.inspect = { ...this.data.server.inspect, [key]: next };
    const count = this._rootElement?.querySelector('.ic-inspect-count');
    if (count !== null && count !== undefined) count.textContent = String(inspectDoneCount(this.data.server));
  }

  handleConfigChange(e: Event): void {
    this.config = detailOf(e, 'config:change');
    this.data.server.configCount = { installed: this.config.installed.length, included: this.config.included.reduce((a, i) => a + i.qty, 0) };
    this.rerenderProgress();
  }

  /** 저장 — 목업: 결과 카드만. 실제는 한 트랜잭션(product 찾거나 만듦 → 서버 unit → 자식 unit → PN 미등록이면 카탈로그 요청) */
  handleSave(): void {
    const s = this.data.server;
    const saved = {
      unit_id: 10231,
      hclt_number: s.catalog.productId === null ? 'HCL-SV-000043' : 'HCL-SV-000042',
      installed_count: this.config.installed.length,
      included_count: this.config.included.reduce((a, i) => a + i.qty, 0),
      is_new_product: s.catalog.productId === null,
      inspect_done: inspectDoneCount(s), inspect_total: INSPECT_TOTAL,
    };
    s.saved = saved;
    this.refreshRegion('.ic-saved-slot', () => renderSaved(saved));
    this.rerenderProgress();
    this._rootElement?.querySelector('.ic-saved')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // ─── 스캔 목록 (2026-10-06) — 버블(실물) · 카드(자리) · 틀(라벨 번호 순서). 시리얼은 스캐너가 진실, 사진은 PN 만 ───

  /** 엔터 = 바코드 하나. 빈 엔터 = 열린 실물 끝(틀을 다 못 채운 라벨) */
  handleScanKey(e: Event, el: HTMLElement): void {
    if ((e as KeyboardEvent).key !== 'Enter') return;
    e.preventDefault();
    const input = el as HTMLInputElement;
    const code = input.value.trim();
    input.value = '';
    if (code === '') this.data.server.scan.open = null; else this.addScan(code);
    this.rerenderHead();
  }

  /** 찍은 값 — 꼴을 안 본다(PN 도): 열린 버블의 다음 자리에, 열린 버블이 없으면 새 버블(활성 카드로, 없으면 풀)의 1번 자리에. 자리가 다 차면 닫힌다. 카드는 구매 품목 · 사진 · PN 직접 칸으로만 */
  private addScan(code: string): void {
    const { scan } = this.data.server;
    const open = scan.open === null ? undefined : scan.lines[scan.open];
    if (open !== undefined) {
      const frame = frameOf(targetOf(scan, open.target));
      const slot = nextFrameTypeOf(open, frame);
      assert(slot !== null, '열린 버블인데 빈 자리가 없다 — 자리가 다 차면 닫혔어야 한다');
      open.numbers = { ...open.numbers, [slot]: code };
      if (nextFrameTypeOf(open, frame) === null) scan.open = null;
      this.syncFromScan();
      return;
    }
    const frame = frameOf(targetOf(scan, scan.activeTarget));
    const first = frame.types[0];
    assert(first !== undefined, '틀에 자리가 하나도 없다');
    const line: ScanLine = { numbers: { [first]: code }, brand: scan.brand, target: scan.activeTarget, scanned_at: new Date().toISOString() };
    scan.lines.push(line);
    scan.open = nextFrameTypeOf(line, frame) === null ? null : scan.lines.length - 1;
    this.advanceActiveIfFull();
    this.syncFromScan();
  }

  /** 활성 카드의 자리가 다 찼으면 다음 빈 칸 있는 카드로(없으면 풀) — 구매 품목을 순서대로 찍을 때 카드를 안 눌러도 되게 */
  private advanceActiveIfFull(): void {
    const { scan } = this.data.server;
    const active = targetOf(scan, scan.activeTarget);
    if (active === null || active.qty === null || linesOfTarget(scan, active.key).length < active.qty) return;
    const from = scan.targets.findIndex((target) => target.key === active.key);
    const next = scan.targets.slice(from + 1).find((target) => target.qty === null || linesOfTarget(scan, target.key).length < target.qty);
    scan.activeTarget = next === undefined ? null : next.key;
  }

  /** PN 으로 카드를 찾아 활성. 없으면 PN 직접 카드(수 모름)를 만들어 활성 */
  private activateOrCreateTarget(pn: string, brand: ScanBrand): void {
    const { scan } = this.data.server;
    const found = scan.targets.find((target) => target.option_part === pn);
    const key = found === undefined ? this.addTarget({ key: `manual-${pn}`, brand, option_part: pn, description: '', category: null, qty: null, source: 'manual' }) : found.key;
    scan.activeTarget = key;
    scan.open = null;
  }

  /** 카드를 더한다 — 같은 PN 카드가 있으면 그것(수 모름 카드면 채우고, 같은 출처면 수를 더한다). 돌려주는 것은 그 카드의 key */
  private addTarget(target: ScanTarget): string {
    const { scan } = this.data.server;
    const found = scan.targets.find((candidate) => candidate.option_part === target.option_part);
    if (found === undefined) { scan.targets.push(target); return target.key; }
    if (found.qty === null && target.qty !== null) Object.assign(found, { description: target.description, category: target.category, qty: target.qty, source: target.source });   // PN 직접 카드가 뒤에 온 견적 · 사진 정보를 받는다
    else if (found.qty !== null && target.qty !== null && found.source === target.source) found.qty += target.qty;
    return found.key;
  }

  /** ② 의 시리얼 · SID · CT 는 섀시 카드의 첫 버블(없으면 첫 버블)에서 — 빈 칸만 채운다. ③ 트레이는 부품 카드의 버블들(있을 때만 — 사진이 준 트레이를 스캔이 덮는다) */
  private syncFromScan(): void {
    const s = this.data.server;
    const { scan } = s;
    const chassis = scan.targets.find((target) => target.category === 'chassis');
    const headIndex = chassis === undefined ? 0 : linesOfTarget(scan, chassis.key)[0] ?? 0;
    const head = scan.lines[headIndex];
    if (head !== undefined) {
      const unit: UnitInput = { ...s.unit };
      if (unit.serial_number === '' && head.numbers.serial !== undefined) unit.serial_number = head.numbers.serial;
      if (unit.security_id === '' && head.numbers.security_id !== undefined) unit.security_id = head.numbers.security_id;
      if (unit.tracking_number === '' && head.numbers.tracking_number !== undefined) unit.tracking_number = head.numbers.tracking_number;
      s.unit = unit;
    }
    const parts: api.PartCandidate[] = scan.targets.filter((target) => target.category !== null && target.category !== 'chassis').flatMap((target) =>
      linesOfTarget(scan, target.key).map((index) => {
        const line = scan.lines[index];
        assert(line !== undefined && target.category !== null, '부품 카드의 버블이 없다');
        return { vendor: target.brand, option_part: target.option_part, category: target.category, description: target.description, serial: line.numbers.serial ?? null, unit_id: null };
      }));
    if (parts.length > 0) this.editor.setPhotoParts(parts);
  }

  handleScanBrand(_e: Event, el: HTMLElement): void {
    this.data.server.scan.brand = (el as HTMLSelectElement).value as ScanBrand;
    this._rootElement?.querySelector<HTMLInputElement>('.ic-scan-input')?.focus();
  }

  /** 확정 — 열린 버블을 닫는다(빈 엔터와 같다). 선택 자리가 없는 라벨 */
  handleBubbleClose(e: Event): void {
    e.stopPropagation();
    this.data.server.scan.open = null;
    this.rerenderHead();
  }

  /** 칩 끌기 — 버블 안의 번호 하나. 버블 끌기와 겹치지 않게 막는다 */
  handleChipDragStart(e: Event, el: HTMLElement): void {
    e.stopPropagation();
    (e as DragEvent).dataTransfer?.setData('text/plain', `chip:${el.dataset.index}:${el.dataset.type}`);
  }

  /** 칩을 자리에 놓으면 — 빈 자리면 옮기고, 찬 자리면 둘을 맞바꾼다. 다른 버블의 자리여도 된다(옮긴 뒤 비는 버블은 지운다) */
  handleDropOnSlot(e: Event, el: HTMLElement): void {
    e.preventDefault();
    e.stopPropagation();
    const data = (e as DragEvent).dataTransfer?.getData('text/plain') ?? '';
    if (!data.startsWith('chip:')) { this.handleDropOnTarget(e, el.closest<HTMLElement>('[data-action="target"]') ?? el); return; }
    const [, fromIndex, fromType] = data.split(':');
    const toIndex = Number(el.dataset.index);
    const toType = el.dataset.type as ScanType | undefined;
    assert(fromIndex !== undefined && fromType !== undefined && toType !== undefined, '칩 놓기 — 자리 정보가 없다');
    this.moveChip(Number(fromIndex), fromType as ScanType, toIndex, toType);
    this.rerenderHead();
  }

  private moveChip(fromIndex: number, fromType: ScanType, toIndex: number, toType: ScanType): void {
    const { scan } = this.data.server;
    const from = scan.lines[fromIndex];
    const to = scan.lines[toIndex];
    assert(from !== undefined && to !== undefined, '칩 옮기기 — 버블이 없다');
    const value = from.numbers[fromType];
    if (value === undefined || (fromIndex === toIndex && fromType === toType)) return;
    const swapped = to.numbers[toType];
    const fromNumbers = { ...from.numbers };
    delete fromNumbers[fromType];
    from.numbers = fromNumbers;
    if (swapped !== undefined) from.numbers = { ...from.numbers, [fromType]: swapped };
    to.numbers = { ...to.numbers, [toType]: value };
    if (Object.keys(from.numbers).length === 0) {
      scan.lines.splice(fromIndex, 1);
      scan.open = scan.open === null || scan.open === fromIndex ? null : scan.open > fromIndex ? scan.open - 1 : scan.open;
      scan.selected = new Set();
    }
    this.syncFromScan();
  }

  /** 버블을 누르면 고름 · 다시 누르면 풂(종류 셀렉터 클릭은 제외). 고른 것은 카드를 누르면 거기로 */
  handleBubble(e: Event, el: HTMLElement): void {
    if ((e.target as HTMLElement).closest('button, .ic-chip') !== null) return;
    const index = Number(el.dataset.index);
    const { selected } = this.data.server.scan;
    if (selected.has(index)) selected.delete(index); else selected.add(index);
    this.rerenderHead();
  }

  handleBubbleRemove(_e: Event, el: HTMLElement): void {
    const index = Number(el.dataset.index);
    const { scan } = this.data.server;
    scan.lines.splice(index, 1);
    scan.selected = new Set();
    scan.open = scan.open === null || scan.open === index ? null : scan.open > index ? scan.open - 1 : scan.open;
    this.syncFromScan();
    this.rerenderHead();
  }

  handleBubbleOut(_e: Event, el: HTMLElement): void {
    const index = Number(el.dataset.index);
    this.moveLines([index], null);
    this.rerenderHead();
  }

  /** 카드를 누르면 — 고른 버블이 있으면 그것들을 넣고, 없으면 이 카드를 활성(다시 누르면 풀로) */
  handleTarget(_e: Event, el: HTMLElement): void {
    const key = el.dataset.key;
    const { scan } = this.data.server;
    assert(key !== undefined, '카드 key 가 없다');
    if (scan.selected.size > 0) this.moveLines([...scan.selected], key);
    else scan.activeTarget = scan.activeTarget === key ? null : key;
    scan.open = null;
    this.rerenderHead();
  }

  handleTargetRemove(e: Event, el: HTMLElement): void {
    e.stopPropagation();
    const key = el.dataset.key;
    const { scan } = this.data.server;
    scan.targets = scan.targets.filter((target) => target.key !== key);
    for (const line of scan.lines) if (line.target === key) line.target = null;
    if (scan.activeTarget === key) scan.activeTarget = null;
    scan.open = null;
    this.syncFromScan();
    this.rerenderHead();
  }

  /** 버블들을 카드(null = 풀)로 옮긴다. 고름은 푼다 */
  private moveLines(indexes: number[], key: string | null): void {
    const { scan } = this.data.server;
    for (const index of indexes) {
      const line = scan.lines[index];
      assert(line !== undefined, `스캔 버블 ${index} 가 없다`);
      line.target = key;
      if (scan.open === index) scan.open = null;
    }
    scan.selected = new Set();
    this.syncFromScan();
  }

  handleBubbleDragStart(e: Event, el: HTMLElement): void {
    const index = Number(el.dataset.index);
    (e as DragEvent).dataTransfer?.setData('text/plain', String(index));
    this.data.server.scan.selected.add(index);
    el.classList.add('is-selected');
  }

  handleDragOver(e: Event): void { e.preventDefault(); }

  /** 카드 · 풀에 떨어뜨리면 — 끌던 버블과 고른 버블들이 거기로 */
  handleDropOnTarget(e: Event, el: HTMLElement): void {
    e.preventDefault();
    const key = el.dataset.key ?? null;
    const { selected } = this.data.server.scan;
    const data = (e as DragEvent).dataTransfer?.getData('text/plain') ?? '';
    if (data.startsWith('chip:')) return;   // 칩은 자리(@slot)에만 놓는다
    const dragged = Number(data);
    if (data !== '' && !Number.isNaN(dragged)) selected.add(dragged);
    this.moveLines([...selected], key);
    this.rerenderHead();
  }

  handleScanPurchase(): void {
    this.data.server.scan.isPurchaseOpen = !this.data.server.scan.isPurchaseOpen;
    this.rerenderHead();
  }

  /** 구매 줄 하나 → 카드(출처 구매) */
  handleScanPurchaseLine(_e: Event, el: HTMLElement): void {
    const purchaseLine = MOCK_PURCHASE_LINES[Number(el.dataset.index)];
    assert(purchaseLine !== undefined, '구매 줄이 없다');
    this.addPurchaseTarget(purchaseLine);
    this.rerenderHead();
  }

  handleScanPurchaseAll(): void {
    for (const purchaseLine of MOCK_PURCHASE_LINES) this.addPurchaseTarget(purchaseLine);
    this.data.server.scan.isPurchaseOpen = false;
    this.rerenderHead();
  }

  private addPurchaseTarget(purchaseLine: typeof MOCK_PURCHASE_LINES[number]): void {
    this.addTarget({ key: `purchase-${purchaseLine.option_part}`, brand: 'HPE', option_part: purchaseLine.option_part, description: purchaseLine.description, category: purchaseLine.category, qty: purchaseLine.qty, source: 'purchase' });
  }

  /** PN 직접 — 칸에 친 PN 으로 카드(수 모름)를 만들어 활성 */
  handleScanNewTarget(): void {
    const input = this._rootElement?.querySelector<HTMLInputElement>('[data-action="scan-new-pn"]');
    const pn = input?.value.trim().toUpperCase() ?? '';
    if (pn === '') return;
    this.activateOrCreateTarget(pn, this.data.server.scan.brand);
    this.rerenderHead();
  }

  /** 사진이 읽은 PN 들을 카드로(출처 사진) — 섀시 PN 은 섀시 카드(1), 부품 줄은 수량대로. 그리고 끝 4자 일치로 풀의 버블을 앉힌다 */
  private addPhotoTargets(kept: PhotoLine[]): void {
    const chassisPn = kept.find((line): line is Extract<PhotoLine, { kind: 'chassis' }> => line.kind === 'chassis' && line.field === 'catalog.query');
    if (chassisPn !== undefined) this.addTarget({ key: `photo-${chassisPn.value}`, brand: this.data.server.scan.brand, option_part: chassisPn.value, description: '섀시', category: 'chassis', qty: 1, source: 'photo' });
    for (const line of kept) {
      if (line.kind !== 'part' || !line.keep) continue;
      this.addTarget({ key: `photo-${line.part.option_part}`, brand: this.data.server.scan.brand, option_part: line.part.option_part, description: line.part.description, category: line.part.category, qty: line.qty, source: 'photo' });
    }
    this.placePoolByPhoto(kept);
  }

  /** 풀의 버블 → 사진 시리얼 끝 4자가 맞는 PN 의 카드로. 사진 속 시리얼은 저장 안 하고 맞추는 데만 */
  private placePoolByPhoto(kept: PhotoLine[]): void {
    const { scan } = this.data.server;
    const tail = (value: string): string => value.slice(-4).toUpperCase();
    const chassisPn = kept.find((line): line is Extract<PhotoLine, { kind: 'chassis' }> => line.kind === 'chassis' && line.field === 'catalog.query');
    const chassisSerial = kept.find((line): line is Extract<PhotoLine, { kind: 'chassis' }> => line.kind === 'chassis' && line.field === 'unit.serial_number');
    const partSerials = expandPhotoParts(kept).filter((part) => part.serial !== null);
    const targetByPn = (pn: string): string | null => scan.targets.find((target) => target.option_part === pn)?.key ?? null;
    scan.lines.forEach((line, index) => {
      if (line.target !== null || line.numbers.serial === undefined) return;
      const serial = line.numbers.serial;
      if (chassisPn !== undefined && chassisSerial !== undefined && tail(chassisSerial.value) === tail(serial)) { line.target = targetByPn(chassisPn.value); return; }
      const part = partSerials.find((candidate) => candidate.serial !== null && tail(candidate.serial) === tail(serial));
      if (part !== undefined) line.target = targetByPn(part.option_part);
      if (scan.open === index && line.target !== null) scan.open = null;
    });
    this.syncFromScan();
  }

  /** 자동 배치 — 먼저 사진 끝 4자 일치, 남은 풀 버블은 찍은 순서대로 빈 칸 있는 카드에 */
  handleScanPlaceAll(): void {
    const s = this.data.server;
    this.placePoolByPhoto(s.photo.lines.filter((line) => line.keep));
    const { scan } = s;
    for (const line of scan.lines) {
      if (line.target !== null) continue;
      const slot = scan.targets.find((target) => target.qty !== null && linesOfTarget(scan, target.key).length < target.qty);
      if (slot === undefined) break;
      line.target = slot.key;
    }
    scan.selected = new Set();
    scan.open = null;
    this.syncFromScan();
    this.rerenderHead();
  }

  /** 시연 — 구매 카드 넷을 깔고, 카드를 누르듯 활성해 가며 섀시(시리얼 · SID · 빈 엔터) · SSD 둘(시리얼 · 빈 엔터) · 메모리 둘을 찍은 것처럼. 사진 목업의 시리얼 조각과 끝 4자가 맞게 */
  handleScanMock(): void {
    for (const purchaseLine of MOCK_PURCHASE_LINES) this.addPurchaseTarget(purchaseLine);
    const steps: ReadonlyArray<string | { card: string }> = [{ card: 'P19766-B21' }, 'CZ20350K7X', '3K7X-9QW2-ABCD-EF12', '', { card: 'P18434-B21' }, 'S4XQNB70K', '', 'S4XQNB71L', '', { card: 'P00924-B21' }, '8A1B2C3D', '8A1B2C3E'];
    for (const step of steps) {
      if (typeof step !== 'string') { this.activateOrCreateTarget(step.card, 'HPE'); continue; }
      if (step === '') this.data.server.scan.open = null; else this.addScan(step);
    }
    this.rerenderHead();
  }

  /** 같은 구성으로 한 대 더 — ① ③ 유지, ② 와 사진 · 결과를 비움 */
  handleNextUnit(): void {
    const s = this.data.server;
    s.unit = { ...EMPTY_UNIT, warehouse: s.unit.warehouse, zone: s.unit.zone, cost_amount: s.unit.cost_amount, cost_currency: s.unit.cost_currency, origin: s.unit.origin };
    s.fromPhoto = new Set();
    s.scan = { ...s.scan, lines: [], activeTarget: null, open: null, selected: new Set(), isPurchaseOpen: false };   // 카드(구매 품목)는 남긴다 — 같은 발주의 다음 한 대
    s.photo = { status: 'idle', lines: [], error: '' };
    s.saved = null;
    this.refreshRegion('.ic-saved-slot', () => '');
    this.rerenderHead();
    this.rerenderProgress();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private rerenderProgress(): void {
    this.refreshRegion('.ic-progress-wrap', () => renderProgress(this.data.server));
  }

  private rerenderSuggest(): void {
    this.refreshRegion('.ic-suggest', () => renderSuggest(this.data.server));
  }

  private rerenderFound(): void {
    this.refreshRegion('.ic-catalog-found', () => renderCatalogFound(this.data.server));
  }

  private rerenderHead(): void {
    this.refreshRegion('.ic-head', () => { const s = this.data.server; return renderScan(s) + renderPhoto(s) + renderCatalog(s) + renderUnit(s); });
    this._rootElement?.querySelector<HTMLInputElement>('.ic-scan-input')?.focus();
  }
}

export type { ServerSection };
